import React, { Component } from "react";
import { FaCircle, FaStop, FaRedo, FaVideo } from "react-icons/fa";
import { Translate, I18n } from "react-redux-i18n";
import alertify from "alertifyjs";

const MAX_RECORD_SECONDS = 60;

class StoreReelVideoRecorder extends Component {
  videoRef = React.createRef();
  mediaRecorder = null;
  stream = null;
  chunks = [];
  timerId = null;

  state = {
    recording: false,
    seconds: 0,
    previewUrl: "",
    videoFile: null,
    starting: false,
  };

  componentDidMount() {
    if (this.props.active) {
      this.startCamera();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.active && !prevProps.active) {
      this.startCamera();
    }
    if (!this.props.active && prevProps.active) {
      this.resetRecorder();
    }
  }

  componentWillUnmount() {
    this.resetRecorder();
  }

  stopStream = () => {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.videoRef.current) {
      this.videoRef.current.srcObject = null;
    }
  };

  clearTimer = () => {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  resetRecorder = () => {
    this.clearTimer();
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
    this.mediaRecorder = null;
    this.chunks = [];
    this.stopStream();
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }
    this.setState({
      recording: false,
      seconds: 0,
      previewUrl: "",
      videoFile: null,
      starting: false,
    });
    this.props.onVideoChange(null);
  };

  startCamera = async () => {
    if (this.stream || this.state.starting) return;

    this.setState({ starting: true });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      this.stream = stream;
      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
      }
      this.setState({ starting: false });
    } catch (error) {
      this.setState({ starting: false });
      alertify.warning(I18n.t("store_reels_camera_error"));
    }
  };

  startRecording = () => {
    if (!this.stream || this.state.recording) return;

    this.chunks = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";

    try {
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
    } catch (error) {
      alertify.warning(I18n.t("store_reels_camera_error"));
      return;
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: "video/webm" });
      const previewUrl = URL.createObjectURL(blob);
      const videoFile = new File([blob], `reel-${Date.now()}.webm`, {
        type: "video/webm",
      });

      this.setState({
        previewUrl,
        videoFile,
        recording: false,
        seconds: 0,
      });
      this.props.onVideoChange(videoFile);
      this.stopStream();
    };

    this.mediaRecorder.start();
    this.setState({ recording: true, seconds: 0, previewUrl: "", videoFile: null });
    this.props.onVideoChange(null);

    this.timerId = setInterval(() => {
      this.setState((prevState) => {
        const nextSeconds = prevState.seconds + 1;
        if (nextSeconds >= MAX_RECORD_SECONDS) {
          this.stopRecording();
          return { seconds: MAX_RECORD_SECONDS };
        }
        return { seconds: nextSeconds };
      });
    }, 1000);
  };

  stopRecording = () => {
    this.clearTimer();
    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
  };

  onRerecord = () => {
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }
    this.setState({
      previewUrl: "",
      videoFile: null,
      seconds: 0,
      recording: false,
    });
    this.props.onVideoChange(null);
    this.startCamera();
  };

  render() {
    const { recording, seconds, previewUrl, starting } = this.state;
    const hasRecording = Boolean(previewUrl);

    return (
      <div className="store-add-reel-recorder">
        <div className="store-add-reel-recorder__stage">
          {hasRecording ? (
            <video
              src={previewUrl}
              className="store-add-reel-recorder__video"
              controls
              playsInline
            />
          ) : (
            <video
              ref={this.videoRef}
              className="store-add-reel-recorder__video"
              autoPlay
              muted
              playsInline
            />
          )}
          {!hasRecording && !starting && (
            <div className="store-add-reel-recorder__overlay">
              <FaVideo />
              <span>
                <Translate value="store_reels_record_hint" />
              </span>
            </div>
          )}
        </div>

        <div className="store-add-reel-recorder__meta">
          {recording && (
            <span className="store-add-reel-recorder__timer">
              {String(Math.floor(seconds / 60)).padStart(2, "0")}:
              {String(seconds % 60).padStart(2, "0")}
            </span>
          )}
        </div>

        <div className="store-add-reel-recorder__actions">
          {!hasRecording && !recording && (
            <button
              type="button"
              className="store-add-reel-recorder__btn store-add-reel-recorder__btn--record"
              onClick={this.startRecording}
              disabled={starting || !this.stream}
            >
              <FaCircle />
              <Translate value="store_reels_start_record" />
            </button>
          )}

          {recording && (
            <button
              type="button"
              className="store-add-reel-recorder__btn store-add-reel-recorder__btn--stop"
              onClick={this.stopRecording}
            >
              <FaStop />
              <Translate value="store_reels_stop_record" />
            </button>
          )}

          {hasRecording && (
            <button
              type="button"
              className="store-add-reel-recorder__btn store-add-reel-recorder__btn--secondary"
              onClick={this.onRerecord}
            >
              <FaRedo />
              <Translate value="store_reels_rerecord" />
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default StoreReelVideoRecorder;
